'use client';

import { Button } from '@pickleballinc/ui/components/ui/button';
import { ReactNode, useEffect, useRef, useState } from 'react';

interface DetailsCardProps {
	title: string;
	titleIcon?: ReactNode;
	subtitle?: ReactNode | string;
	content: ReactNode | string;
	expandable?: boolean;
	expandedByDefault?: boolean;
	isSubtitleCta?: boolean;
	customTitle?: boolean;
	isTitleOutsideBox?: boolean;
	titleClassName?: string;
}

export const DetailsCard = (props: DetailsCardProps) => {
	const {
		title,
		subtitle,
		content,
		expandable = true,
		expandedByDefault = false,
		isSubtitleCta = false,
		titleIcon,
		customTitle,
		isTitleOutsideBox = false,
		titleClassName
	} = props;

	const [showButton, setShowButton] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (contentRef.current) {
			// Check if content height exceeds the specified height
			if (contentRef.current.scrollHeight > 48 * 4) {
				setShowButton(true);
			}
		}
	}, [content]);

	const [isExpanded, setIsExpanded] = useState(expandedByDefault);

	const handleToggle = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<div className="flex flex-col gap-2">
			{isTitleOutsideBox ? <div className={`text-lg font-bold text-darkorange-400 sm:text-2xl ${titleClassName}`}>{title}</div> : null}

			<div className={`relative flex flex-col gap-4 h-full rounded-lg bg-white p-4 sm:gap-6 sm:p-8 ${expandable ? 'pb-10 sm:pb-12' : ''} `}>
				{(title || subtitle) && !customTitle && !isTitleOutsideBox ? (
					<div className="flex flex-row items-center justify-between">
						<div className="text-lg font-bold text-darkorange-400 sm:text-2xl">{title}</div>
						<div className={`${isSubtitleCta ? 'hidden sm:block' : ''}`}>{subtitle}</div>
					</div>
				) : null}
				{(title || subtitle) && customTitle && !isTitleOutsideBox ? (
					<div className="flex flex-row justify-between">
						<div className="flex-1 font-bold text-gray-900">
							<span className="mr-2">{titleIcon}</span>
							{title}
						</div>
						{!!subtitle && <div className="flex-1">{subtitle}</div>}
					</div>
				) : null}

				<div ref={contentRef} className={`${!expandable || isExpanded || !showButton ? 'h-auto' : 'h-48 overflow-hidden text-gray-900'}`}>
					{content}
				</div>
				{expandable && !isExpanded ? (
					<div
						className="pointer-events-none absolute inset-x-0 bottom-0 h-32 rounded-b-lg"
						style={{
							background: showButton ? 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))' : ''
						}}
					/>
				) : null}
				{subtitle && isSubtitleCta ? <div className="ml-auto sm:hidden">{subtitle}</div> : null}
				{expandable && showButton ? (
					<Button onClick={handleToggle} className="absolute bottom-4 right-4 sm:right-6" variant="link">
						{isExpanded ? 'See Less' : 'See More'}
					</Button>
				) : null}
			</div>
		</div>
	);
};
